/**
 * DEV-1044 — redeeming a packhouse's grower invite.
 *
 * The link a grower receives is `https://<producer-app-host>/invite?token=…`,
 * where the host is whichever tenant app serves their country (theboer.co.za
 * for ZA; another domain elsewhere — the backend resolves it, see
 * `Services/Portal/portal-invite-landing.ts`). The token is single-use,
 * email-bound and expires in seven days.
 *
 * Three things make this awkward, and all three are why the token is parked in
 * sessionStorage rather than simply read where it is used:
 *
 *  1. **The redeem needs an authenticated caller**, and the visitor arriving on
 *     this link usually has no account at all. So the token has to survive a
 *     full round trip out to Auth0 and back to `/auth0/callback` — a different
 *     path, with the original query string gone.
 *  2. **The token must not linger in the address bar.** It is
 *     security-equivalent to a password: it would be copied into a support
 *     ticket, shared in a screenshot, or left in browser history. It is
 *     stripped from the URL the moment it is read.
 *  3. **It is consumed exactly once.** `take()` clears before returning, so a
 *     retry after a failed redeem cannot burn a second attempt on a token the
 *     server already marked REDEEMED.
 *
 * sessionStorage, not localStorage: one tab, one invite, gone when the tab is.
 */
import { redeemInvite } from "./session";

const PENDING_INVITE_KEY = "metaship:pendingInviteToken";
/** INS-37 — `kind=programme` on the same /invite path. Absent means a portal invite. */
const PENDING_INVITE_KIND_KEY = "metaship:pendingInviteKind";
export const PROGRAMME_INVITE_KIND = "programme";
export const INVITE_PATH = "/invite";

export type PendingInviteKind = "portal" | "programme";

/**
 * Read `?token=` on the invite path, park it, and scrub the URL.
 * Safe to call on every boot — a no-op anywhere else.
 */
export function captureInviteToken(pathname: string, search: string): void {
  if (pathname !== INVITE_PATH) return;
  let token: string | null = null;
  let kind: string | null = null;
  try {
    const params = new URLSearchParams(search);
    token = params.get("token");
    kind = params.get("kind");
  } catch {
    return;
  }
  if (!token || !token.trim()) return;

  try {
    sessionStorage.setItem(PENDING_INVITE_KEY, token.trim());
    // A portal link must not inherit a programme kind left by an earlier tab
    // visit, and a programme link must not be redeemed on the portal endpoint.
    if (kind === PROGRAMME_INVITE_KIND) sessionStorage.setItem(PENDING_INVITE_KIND_KEY, PROGRAMME_INVITE_KIND);
    else sessionStorage.removeItem(PENDING_INVITE_KIND_KEY);
  } catch {
    // Private mode / storage disabled. The redeem below simply will not run;
    // the visitor still reaches the normal sign-in, which is a worse outcome
    // than being let straight in but not a broken one.
  }
  // Land on "/" with no token in the bar. replaceState, not assign: a reload
  // must not re-trigger the Auth0 round trip from a URL that no longer has it.
  try {
    window.history.replaceState({}, "", "/");
  } catch {
    /* non-fatal */
  }
}

export function hasPendingInvite(): boolean {
  try {
    return !!sessionStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return false;
  }
}

export function pendingInviteKind(): PendingInviteKind {
  try {
    return sessionStorage.getItem(PENDING_INVITE_KIND_KEY) === PROGRAMME_INVITE_KIND ? "programme" : "portal";
  } catch {
    return "portal";
  }
}

export function isProgrammeInvitePending(): boolean {
  return hasPendingInvite() && pendingInviteKind() === "programme";
}

/** Read without clearing. Programme redeem retries (firm name, org choice) need the token again. */
export function peekInviteToken(): string | null {
  try {
    const token = sessionStorage.getItem(PENDING_INVITE_KEY);
    return token?.trim() || null;
  } catch {
    return null;
  }
}

/** Forget the parked invite. Programme redeem calls this only on a terminal outcome. */
export function clearParkedInvite(): void {
  try {
    sessionStorage.removeItem(PENDING_INVITE_KEY);
    sessionStorage.removeItem(PENDING_INVITE_KIND_KEY);
  } catch {
    /* already gone */
  }
}

/** Read and clear in one step — a portal token is never offered twice. */
function takeInviteToken(): string | null {
  const token = peekInviteToken();
  clearParkedInvite();
  return token;
}

export interface RedeemedInvite {
  organisationId: number;
  organisationName: string;
}

/**
 * Redeem a parked invite, if there is one. Returns null when there was nothing
 * to redeem OR when the redeem failed.
 *
 * **Never throws.** A failed redeem must not strand the visitor on an error
 * page: they are still a legitimately signed-in user, and the normal access
 * gate (organisation picker, or "no access") is a far better place to land than
 * a stack trace. The common failures are all benign-looking to the user —
 * the link expired, it was already used, or it was addressed to a different
 * mailbox than the one they just signed in with.
 */
export async function redeemPendingInvite(bearer: string): Promise<RedeemedInvite | null> {
  // INS-37 — a programme invite is redeemed on its own endpoint, after the
  // shell has collected a firm name or an organisation choice. Taking it here
  // would burn the token on the portal route and drop that conversation.
  if (pendingInviteKind() === "programme") return null;
  const token = takeInviteToken();
  if (!token) return null;
  try {
    return await redeemInvite(token, bearer);
  } catch {
    return null;
  }
}
